import React, { useMemo } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import {
  ChallengeHomeCard,
  ChallengeHomeCardProps,
} from '@fhs/loyalty/src/components/challenge-home-card';
import { ChallengeHomeCardSkeleton } from '@fhs/loyalty/src/components/challenge-home-card-skeleton';
import { createChallengeHomeCardProps } from '@fhs/loyalty/src/utils';
import { OrderDisplaySkeleton, RapidRescueCard } from '@fhs/ui';
import { OrderDisplay } from '@fhs/ui/src/components/order-display';

import { ScreenHomeProps } from '../types';

type HomeCardProps = Pick<
  ScreenHomeProps,
  | 'displayedOrder'
  | 'displayedOrderCardLoading'
  | 'redirectPath'
  | 'onReorder'
  | 'reorderLoading'
  | 'logReorderCardPress'
  | 'challengesData'
  | 'isLoadingChallenges'
  | 'loyaltyUserReady'
  | 'isDesktop'
  | 'onPressChallenge'
  | 'showChallengeInfo'
> & {
  style?: StyleProp<ViewStyle>;
};

export function HomeCard({
  style,
  displayedOrderCardLoading,
  displayedOrder,
  challengesData,
  isLoadingChallenges,
  loyaltyUserReady,
  redirectPath,
  reorderLoading,
  onReorder,
  logReorderCardPress,
  isDesktop,
  onPressChallenge,
  showChallengeInfo,
}: HomeCardProps) {
  const containerStyle = useMemo(() => [styles.container, style], [style]);

  const challengeHomeCardProps = useMemo<ChallengeHomeCardProps | undefined>(() => {
    const firstChallenge = challengesData?.[0];
    if (firstChallenge) {
      return createChallengeHomeCardProps(
        firstChallenge,
        isDesktop,
        onPressChallenge,
        showChallengeInfo
      );
    }
    return undefined;
  }, [challengesData, isDesktop, onPressChallenge, showChallengeInfo]);

  if (displayedOrderCardLoading && !isDesktop) {
    return (
      <View style={containerStyle}>
        <OrderDisplaySkeleton />
      </View>
    );
  }

  if (displayedOrder) {
    return (
      <View style={containerStyle}>
        <OrderDisplay
          type={displayedOrder.type}
          href={redirectPath}
          storeName={displayedOrder.storeName}
          items={displayedOrder.items}
          serviceModeTitle={displayedOrder.serviceModeTitle}
          reorderCallback={onReorder}
          reorderLoading={reorderLoading}
          logReorderCardPress={logReorderCardPress}
        />
      </View>
    );
  }

  if ((isLoadingChallenges || !loyaltyUserReady) && !isDesktop) {
    return (
      <View style={containerStyle}>
        <ChallengeHomeCardSkeleton />
      </View>
    );
  }

  if (challengeHomeCardProps) {
    return (
      <View style={containerStyle}>
        <ChallengeHomeCard {...challengeHomeCardProps} />
      </View>
    );
  }

  if (!isDesktop) {
    return (
      <View style={[styles.rapidRescueContainer, style]}>
        <RapidRescueCard />
      </View>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
  },
  rapidRescueContainer: {
    width: '100%',
    paddingHorizontal: 16,
    flex: 1,
  },
});
